// ============================================
// Vendor Dashboard
// Overview stat cards for order fulfillment
// ============================================

import { useEffect, useState } from 'react';
import {
  CreditCard,
  Loader,
  Truck,
  CheckCircle2,
  Package,
  AlertCircle,
} from 'lucide-react';
import { getAllOrders } from '../../services/firestore/orderService';
import type { Order, OrderStatus } from '../../types/order';

interface StatCard {
  label: string;
  count: number;
  icon: typeof Package;
  colorClass: string;
  bgClass: string;
}

export function VendorDashboard() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadOrders();
  }, []);

  async function loadOrders() {
    try {
      setLoading(true);
      const data = await getAllOrders();
      setOrders(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  }

  function countByStatus(status: OrderStatus): number {
    return orders.filter((o) => o.status === status).length;
  }

  const stats: StatCard[] = [
    {
      label: 'Paid (Ready)',
      count: countByStatus('paid'),
      icon: CreditCard,
      colorClass: 'text-gold',
      bgClass: 'bg-gold/10',
    },
    {
      label: 'Processing',
      count: countByStatus('processing'),
      icon: Loader,
      colorClass: 'text-suboptimal',
      bgClass: 'bg-suboptimal/10',
    },
    {
      label: 'Shipped',
      count: countByStatus('shipped'),
      icon: Truck,
      colorClass: 'text-optimal',
      bgClass: 'bg-optimal/10',
    },
    {
      label: 'Total Orders',
      count: orders.length,
      icon: Package,
      colorClass: 'text-accent-light',
      bgClass: 'bg-accent-light/10',
    },
  ];

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader size={32} className="text-accent-light animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center py-20 gap-3 text-critical">
        <AlertCircle size={24} />
        <p className="text-lg">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-2xl font-display text-text-primary mb-1">Dashboard</h2>
        <p className="text-text-muted text-base">Fulfillment overview for all orders</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => (
          <div
            key={stat.label}
            className="anim-fade-in-up bg-bg-card border border-bg-tertiary rounded-xl p-5"
            style={{ animationDelay: `${i * 0.08}s` }}
          >
            <div className="flex items-center justify-between mb-4">
              <span className="text-text-secondary text-base font-medium">{stat.label}</span>
              <div className={`w-10 h-10 rounded-lg ${stat.bgClass} flex items-center justify-center`}>
                <stat.icon size={20} className={stat.colorClass} />
              </div>
            </div>
            <p className={`text-3xl font-bold ${stat.colorClass}`}>{stat.count}</p>
          </div>
        ))}
      </div>

      {/* Recent orders needing attention */}
      {countByStatus('paid') > 0 && (
        <div
          className="anim-fade-in bg-bg-card border border-gold/30 rounded-xl p-5"
          style={{ animationDelay: '0.4s' }}
        >
          <div className="flex items-center gap-3 mb-3">
            <CheckCircle2 size={20} className="text-gold" />
            <h3 className="text-lg font-medium text-gold">Orders Ready for Fulfillment</h3>
          </div>
          <p className="text-text-secondary text-base">
            You have <span className="text-gold font-semibold">{countByStatus('paid')}</span> paid{' '}
            {countByStatus('paid') === 1 ? 'order' : 'orders'} waiting to be processed.
            Head to the <span className="text-accent-light">Orders</span> tab to begin fulfillment.
          </p>
        </div>
      )}
    </div>
  );
}
